import Lenis from "lenis";
import gsap from "gsap";
import ScrollTrigger from "gsap/ScrollTrigger";
import { replace } from "lodash";
gsap.registerPlugin(ScrollTrigger);

window.addEventListener('DOMContentLoaded', () => {
    console.log('init in the script')

    // Smooth Scroll
    window.scrollTo(0, 0);

    const lenis = new Lenis({
        prevent: (node) => node.id === "modal" || node.id === "mobileNav"
    });

    lenis.on("scroll", ScrollTrigger.update);

    gsap.ticker.add((time) => {
        lenis.raf(time * 1000);
    });

    gsap.ticker.lagSmoothing(0);

    // Initial Load Animation
    const initialLoad = () => {
        console.log('initial load of header')

        const header = document.querySelector("#header");
        lenis.scrollTo(0, 0);

        setTimeout(() => {
            // overlay?.remove();
            header?.classList.remove("translate-y-1/3", "opacity-0");
        }, 800);
    }

    initialLoad()

    // Eye Follow Animation - Optimized with throttling
    const eyes = document.querySelectorAll("[data-animate-eye]");
    if (eyes.length) {
        let ticking = false;

        const updateEyes = (e) => {
            if (!ticking) {
                requestAnimationFrame(() => {
                    console.log('updating eyes')

                    eyes.forEach((eye) => {
                        const eyeball = eye.querySelector(".eye-ball");
                        if (!eyeball) return;

                        const eyeRect = eye.getBoundingClientRect();
                        const centerX = eyeRect.left + eyeRect.width / 2;
                        const centerY = eyeRect.top + eyeRect.height / 2;

                        const moveX = e.clientX - centerX;
                        const moveY = e.clientY - centerY;

                        const distance = Math.hypot(moveX, moveY);

                        // Allowed movement: eye radius minus eyeball radius minus margin
                        const maxMove =
                            eyeRect.width / 2 -
                            eyeball.offsetWidth / 2 -
                            eyeRect.width / 100;

                        // Scale movement to stay inside the eye
                        const scale = Math.min(1, maxMove / distance || 0);
                        const finalX = moveX * scale;
                        const finalY = moveY * scale;

                        gsap.to(eyeball, {
                            x: finalX,
                            y: finalY,
                            duration: 0.2,
                            ease: "power2.out",
                        });
                    });
                    ticking = false;
                });
                ticking = true;
            }
        };

        // Initialize eyeballs
        eyes.forEach((eye) => {
            const eyeball = document.createElement("span");
            eyeball.classList.add("eye-ball");
            eye.append(eyeball);
        });

        // Single event listener for all eyes
        document.addEventListener("mousemove", updateEyes, { passive: true });
    }

    // Cursor Follow - Optimized with GSAP and throttling
    const cursorFollowParents = document.querySelectorAll(".cursor-parent");

    const cursorAnimation = (parents) => {
        if (parents) {
            parents.forEach((parent) => {
                const follower = parent.querySelector(".cursor-follow");

                if (follower) {
                    // Throttle mousemove events for better performance
                    let ticking = false;

                    const updateCursor = (e) => {
                        if (!ticking) {
                            requestAnimationFrame(() => {
                                const rect = parent.getBoundingClientRect();
                                const x = e.clientX - rect.left;
                                const y = e.clientY - rect.top;

                                // Set initial position and then animate with transform
                                follower.style.top = `${y}px`;
                                follower.style.left = `${x}px`;

                                ticking = false;
                            });
                            ticking = true;
                        }
                    };

                    parent.removeEventListener("mousemove", updateCursor, true);
                    parent.addEventListener("mousemove", updateCursor, { passive: true });
                }
            });
        }
    }

    cursorAnimation(cursorFollowParents)

    window.initCursorAnimation = () => {
        const cursorFollowParents = document.querySelectorAll(".cursor-parent");
        cursorAnimation(cursorFollowParents)
    }

    // Logos
    const logoParent = document.querySelector("#logo-parent");
    const heading = document.querySelector("#logo-heading");
    const logos = document.querySelectorAll("[animate-logo]");

    if (heading && logoParent) {
        gsap.from(heading, {
            yPercent: 25,
            opacity: 0,
            duration: 1,
            scrollTrigger: {
                trigger: logoParent,
                start: "top-=200px top",
            },
        });
    }

    if (window.innerWidth >= 1024) {
        logos.forEach((logo, index) => {
            const delayFactor = index % 4;

            gsap.from(logo, {
                opacity: 0,
                duration: 1,
                delay: delayFactor * 0.5,
                scrollTrigger: {
                    trigger: logoParent,
                    start: "top-=200px top",
                },
            });
        });
    }

    // What We Do
    const whatWeDoParent = document.querySelector(".wwd-parent");

    if (whatWeDoParent) {
        const rows = whatWeDoParent.querySelectorAll('.wwd-row')

        const timeline = gsap.timeline({
            scrollTrigger: {
                trigger: whatWeDoParent,
                start: "top-=200px center",
            },
        });

        rows?.forEach((row, parentIndex) => {
            const innerChildren = row.querySelectorAll('.wwd-child');

            if (window.innerWidth >= 1440) {
                if (innerChildren.length) {
                    innerChildren.forEach((innerChild, index) => {
                        timeline.from(innerChild, {
                            xPercent: index === 0 ? -100 : 100,
                            duration: 1,
                            ease: "bounce.out",
                        }, parentIndex)
                    })
                } else {
                    timeline.from(row, {
                        xPercent: parentIndex % 2 === 0 ? -120 : 120,
                        duration: 0.75,
                    })
                }
            } else {
                if (innerChildren.length) {
                    innerChildren.forEach((innerChild, index) => {
                        timeline.from(innerChild, {
                            xPercent: index % 2 === 0 ? -150 : 150,
                            duration: 0.6,
                        })
                    })
                } else {
                    timeline.from(row, {
                        xPercent: parentIndex % 2 === 0 ? -150 : 150,
                        duration: 0.6,
                    })
                }
            }
        })
    }

    // Card Stack
    const cardStackParents = document.querySelectorAll('.stack-animation-parent');

    if (cardStackParents) {
        const offset = window.innerHeight < 1280 ? 56 : 80;
        const offsetCards = window.innerHeight < 1280 ? 0.3 : 0.8;

        cardStackParents?.forEach((parent) => {
            const cards = parent.querySelectorAll('.stack-animation-card');

            if (cards?.length) {
                cards?.forEach((card) => {
                    gsap.set(card, { marginTop: `-${card.clientHeight - offset}px` })
                })

                gsap.from(cards, {
                    y: window.innerHeight,
                    ease: 'none',
                    stagger: 0.5,
                    scrollTrigger: {
                        trigger: parent,
                        pin: true,
                        scrub: true,
                        start: 'top top',
                        end: () => `+=${window.innerHeight * cards.length * offsetCards}px`,
                        snap: {
                            duration: { min: 0.2, max: 3 }, // the snap animation should be at least 0.2 seconds, but no more than 3 seconds (determined by velocity)
                            delay: 0.2, // wait 0.2 seconds from the last scroll event before doing the snapping
                        }
                    }
                })
            }
        })
    }

    // Hero Video scroll
    const homeVideo = document.querySelector('#home-video');
    const homeVideoSpace = document.querySelector('#home-video-space');
    const homeVideoChild = document.querySelector('#home-video > *');
    const homeVideoIframe = document.querySelector('#home-video iframe');

    if (homeVideo && homeVideoSpace) {
        const videoAR = window.innerWidth >= 1024 ? window.innerWidth / window.innerHeight : 4 / 3;
        const iframeAR = 16 / 9;

        gsap.set(homeVideoSpace, {
            aspectRatio: videoAR
        })

        gsap.set(homeVideoChild, {
            aspectRatio: videoAR
        })

        let iframeScale = 1;
        if (videoAR > iframeAR) {
            iframeScale = videoAR / iframeAR;
        } else {
            iframeScale = iframeAR / videoAR;
        }

        gsap.set(homeVideoIframe, {
            scale: iframeScale
        })

        const videoDist = homeVideo.offsetTop;
        const videoSpaceDist = homeVideoSpace.offsetTop;
        const yDist = videoDist - videoSpaceDist;

        const videoScale = homeVideoSpace.clientHeight / homeVideo.clientHeight;

        gsap.from(homeVideoChild, {
            y: -yDist,
            scale: videoScale,
            ease: "none",
            scrollTrigger: {
                trigger: homeVideoSpace,
                start: "top-=200px top",
                end: () => `+=${yDist}px`,
                scrub: true,
                onUpdate: (e) => {
                    if (e.progress > 0.9) {
                        homeVideo.classList.add('show-button');
                    } else {
                        homeVideo.classList.remove('show-button');
                    }
                }
            }
        })
    }

    // Text animation
    const slideUp = document.querySelectorAll('[data-slide-up]');

    const removeExtraSpaces = (el) => {
        const text = el.innerHTML.toString();
        const multiSpaces = /\s{2,}/gm;
        el.innerHTML = text.replaceAll(multiSpaces, '');
    }

    const splitIntoSpans = (el) => {
        removeExtraSpaces(el)

        let words = [];

        if (el.innerHTML.includes('</')) {
            words = [el.innerHTML]
        } else {
            words = el.innerHTML.replaceAll('<br>', ' <br> ').split(' ');
        }

        el.innerHTML = '';
        words?.forEach((word, index) => {
            const span = document.createElement('span');
            if (word.includes('</')) {
                span.classList.add('block');
                span.setAttribute('data-fade', '')
                span.innerHTML = word;
            }
            else if (word.includes('<br>')) {
                span.classList.add('block');
            } else {
                span.classList.add('inline-block', 'pr-[0.35ch]');
                span.innerHTML = word;
            }
            el.append(span)
        })

        return el.querySelectorAll(':scope > span');
    }

    const animation = (els, trigger) => {
        if (els.length > 15 || (els.length === 1 && els[0].getAttribute('data-fade') !== null)) {
            gsap.from(els, {
                opacity: 0.2,
                duration: 1,
                ease: 'power2.inOut',
                scrollTrigger: {
                    trigger: trigger,
                    toggleActions: "play none none none",
                }
            })
        } else {
            gsap.fromTo(els, {
                yPercent: 100,
                clipPath: 'polygon(0 0, 100% 0%, 100% 0, 0 0)',
            }, {
                yPercent: 0,
                clipPath: 'polygon(0 0, 100% 0%, 100% 100%, 0% 100%)',
                duration: 1,
                stagger: 0.1,
                ease: 'power2.inOut',
                scrollTrigger: {
                    trigger: trigger,
                    toggleActions: "play none none none",
                }
            })
        }
    }

    if (slideUp.length) {
        slideUp?.forEach((el) => {
            const spans = el.querySelectorAll(':scope > span');

            if (spans.length) {
                animation(spans, el)
            } else {
                animation(splitIntoSpans(el), el)
            }

        })
    }

    // Simple Slide Up
    const simpleSlide = document.querySelectorAll('[data-simple-slide]');

    if (simpleSlide.length) {
        simpleSlide.forEach((el) => {
            gsap.from(el, {
                y: 20,
                duration: 1.5,
                delay: 0.5,
                ease: "elastic.out",
                scrollTrigger: {
                    trigger: el,
                    toggleActions: "play none none none"
                }
            })

            gsap.from(el, {
                opacity: 0,
                duration: 0.5,
                delay: 0.5,
                ease: "power3.out",
                scrollTrigger: {
                    trigger: el,
                    toggleActions: "play none none none"
                }
            })
        })
    }
})
